import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import { clsx } from 'clsx';
import api from '@/api';
import { fetchByokStatus } from '@/api/aiByok';
import { estimatedUsdFromRow, tokenPartsFromRow } from '@/utils/aiUsageTokens';
import { useAccountRole } from '@/hooks/useAccountRole';
import { SettingsReadOnlyBanner } from '@/components/settings/SettingsReadOnlyBanner';

function utcMonthKey(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

type UsageRow = {
  kind: string;
  count: number;
  embedTotalTokens: number;
  chatPromptTokens: number;
  chatCompletionTokens: number;
  estimatedUsd: number;
};

type UsageItem = { id: string; data: Record<string, unknown> };

export const AiUsageSettings = () => {
  const { t } = useTranslation('settings');
  const { canManageAccount } = useAccountRole();
  const readOnlyOrg = !canManageAccount;
  const [month, setMonth] = useState(() => utcMonthKey(new Date()));
  const [summary, setSummary] = useState<UsageRow[] | null>(null);
  const [events, setEvents] = useState<UsageItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingMode, setBillingMode] = useState<'hosted' | 'byok'>('hosted');

  const monthOptions = useMemo(() => {
    const out: string[] = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
      out.push(utcMonthKey(d));
    }
    return out;
  }, []);

  const loadSummary = useCallback(async (m: string) => {
    const res = await api.get<{ status?: string; rows?: UsageRow[] }>('/ai/usage/summary', { params: { month: m } });
    if (res.data?.status === 'success' && Array.isArray(res.data.rows)) {
      setSummary(res.data.rows);
    } else {
      setSummary([]);
    }
  }, []);

  const loadEvents = useCallback(
    async (m: string, nextCursor: string | null, append: boolean) => {
      const res = await api.get<{ status?: string; items?: UsageItem[]; nextCursor?: string | null }>(
        '/ai/usage',
        { params: { month: m, limit: 25, ...(nextCursor ? { cursor: nextCursor } : {}) } }
      );
      if (res.data?.status !== 'success' || !Array.isArray(res.data.items)) {
        throw new Error('bad_response');
      }
      if (append) {
        setEvents((prev) => [...prev, ...res.data.items!]);
      } else {
        setEvents(res.data.items);
      }
      setCursor(res.data.nextCursor ?? null);
    },
    []
  );

  const refresh = useCallback(async () => {
    setError(null);
    setLoading(true);
    setCursor(null);
    try {
      await Promise.all([loadSummary(month), loadEvents(month, null, false)]);
      const byok = await fetchByokStatus().catch(() => null);
      setBillingMode(byok?.mode === 'byok' ? 'byok' : 'hosted');
    } catch {
      setError(t('aiUsage.error'));
      setSummary([]);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [loadEvents, loadSummary, month, t]);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when month changes
  }, [month]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      await loadEvents(month, cursor, true);
    } catch {
      setError(t('aiUsage.error'));
    } finally {
      setLoadingMore(false);
    }
  };

  const formatKind = (k: string) => {
    if (k === 'smart_search') return t('aiUsage.kindSearch');
    if (k === 'assistant_turn') return t('aiUsage.kindAssistant');
    if (k === 'library_reindex') return t('aiUsage.kindReindex');
    if (k === 'suggest_bookmark') return t('aiUsage.kindSuggestBookmark');
    if (k === 'summarize_bookmark') return t('aiUsage.kindSummarizeBookmark');
    if (k === 'suggest_highlights') return t('aiUsage.kindSuggestHighlights');
    if (k === 'suggest_note_tasks') return t('aiUsage.kindSuggestNoteTasks');
    if (k === 'assistant_agent_turn') return t('aiUsage.kindAssistantAgentTurn');
    if (k === 'assistant_tool_call') return t('aiUsage.kindAssistantToolCall');
    if (k === 'summarize_excerpt') return t('aiUsage.kindSummarizeExcerpt');
    if (k === 'indexing_embed') return t('aiUsage.kindIndexingEmbed');
    if (k === 'youtube_chunk_summary') return t('aiUsage.kindYoutubeChunkSummary');
    if (k === 'youtube_summary_reduce') return t('aiUsage.kindYoutubeSummaryReduce');
    if (k === 'youtube_qa_turn') return t('aiUsage.kindYoutubeQaTurn');
    if (k === 'youtube_embed') return t('aiUsage.kindYoutubeEmbed');
    return k;
  };

  const formatMetaDetails = (row: Record<string, unknown>): string => {
    const meta = row.meta;
    if (!meta || typeof meta !== 'object') return '—';
    const m = meta as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof m.videoId === 'string' && m.videoId.trim()) {
      parts.push(`video: ${m.videoId.trim()}`);
    }
    if (typeof m.chunkIndex === 'number' && typeof m.chunkCount === 'number') {
      parts.push(`chunk ${m.chunkIndex + 1}/${m.chunkCount}`);
    } else if (typeof m.chunkIndex === 'number') {
      parts.push(`chunk ${m.chunkIndex}`);
    }
    if (typeof m.chapterTitle === 'string' && m.chapterTitle.trim()) {
      parts.push(m.chapterTitle.trim().slice(0, 80));
    }
    if (typeof m.transcriptSource === 'string' && m.transcriptSource.trim()) {
      parts.push(String(m.transcriptSource));
    }
    if (typeof m.purpose === 'string' && m.purpose.trim()) {
      parts.push(`purpose: ${m.purpose.trim().slice(0, 40)}`);
    }
    return parts.length ? parts.join(' · ') : '—';
  };

  const formatTokens = (row: Record<string, unknown>) => {
    const { embed: e, chat: c } = tokenPartsFromRow(row);
    if (e && c) return `${e} / ${c}`;
    if (e) return `${e} / —`;
    if (c) return `— / ${c}`;
    return '—';
  };

  const eventsTotals = useMemo(() => {
    let embed = 0;
    let chat = 0;
    let usd = 0;
    for (const ev of events) {
      const row = ev.data;
      const p = tokenPartsFromRow(row);
      embed += p.embed;
      chat += p.chat;
      usd += estimatedUsdFromRow(row);
    }
    return { embed, chat, usd };
  }, [events]);

  return (
    <div className="mx-auto max-w-4xl space-y-8 px-4 py-8 md:px-8">
      {readOnlyOrg ? <SettingsReadOnlyBanner /> : null}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">{t('aiUsage.title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('aiUsage.subtitle')}</p>
        <p className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-semibold text-muted-foreground">{t('aiByok.billingMode')}:</span>
          <span
            className={clsx(
              'rounded-full px-2.5 py-0.5 font-semibold',
              billingMode === 'byok' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
            )}
          >
            {billingMode === 'byok' ? t('aiByok.modeByok') : t('aiByok.modeHosted')}
          </span>
          {billingMode === 'byok' ? (
            <Link to="/settings/profile?tab=integrations#byok" className="font-semibold text-primary hover:underline">
              {t('aiUsage.manageByok')}
            </Link>
          ) : null}
        </p>
        <p className="mt-3 rounded-xl border border-border bg-muted/20 p-3 text-xs leading-relaxed text-muted-foreground">
          {t('aiUsage.billingNote')}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {t('aiUsage.month')}
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="min-h-10 rounded-xl border border-border bg-background px-3 text-sm font-medium text-foreground"
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-border px-4 text-sm font-semibold text-foreground hover:bg-muted/40 disabled:opacity-50"
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
          {t('aiUsage.refresh')}
        </button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading && !summary ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-muted-foreground" size={28} />
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">{t('aiUsage.summaryTitle')}</h2>
            <div className="overflow-hidden rounded-2xl border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/40 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">{t('aiUsage.kind')}</th>
                    <th className="px-4 py-3">Count</th>
                    <th className="px-4 py-3">{t('aiUsage.estimatedUsd')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary ?? []).map((r) => (
                    <tr key={r.kind} className="border-t border-border-muted">
                      <td className="px-4 py-3 font-medium">{formatKind(r.kind)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.count}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {r.estimatedUsd > 0 ? `~$${r.estimatedUsd.toFixed(4)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">{t('aiUsage.eventsTitle')}</h2>
            {events.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('aiUsage.empty')}</p>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-border">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/40 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">{t('aiUsage.kind')}</th>
                      <th className="px-4 py-3">{t('aiUsage.outcome')}</th>
                      <th className="px-4 py-3">{t('aiUsage.meta')}</th>
                      <th className="px-4 py-3">{t('aiUsage.tokens')}</th>
                      <th className="px-4 py-3">{t('aiUsage.estimatedUsd')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev) => {
                      const row = ev.data;
                      const est = estimatedUsdFromRow(row);
                      return (
                        <tr key={ev.id} className="border-t border-border-muted">
                          <td className="px-4 py-3 font-medium">{formatKind(String(row.kind ?? ''))}</td>
                          <td className="px-4 py-3 capitalize text-muted-foreground">{String(row.outcome ?? '')}</td>
                          <td className="max-w-[220px] px-4 py-3 text-xs text-muted-foreground break-words">
                            {formatMetaDetails(row)}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{formatTokens(row)}</td>
                          <td className="px-4 py-3 text-muted-foreground">{est > 0 ? `~$${est.toFixed(4)}` : '—'}</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-border bg-muted/30">
                      <td className="px-4 py-3 font-semibold text-foreground" colSpan={3}>
                        {t('aiUsage.totalLoaded')}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-foreground">
                        {`${eventsTotals.embed} / ${eventsTotals.chat}`}
                      </td>
                      <td className="px-4 py-3 font-semibold text-foreground">
                        {eventsTotals.usd > 0 ? `~$${eventsTotals.usd.toFixed(4)}` : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            {cursor && (
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="text-sm font-semibold text-primary hover:underline disabled:opacity-50"
              >
                {loadingMore ? <Loader2 className="inline animate-spin" size={14} /> : null} {t('aiUsage.loadMore')}
              </button>
            )}
          </section>
        </>
      )}
    </div>
  );
};
